import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency';
import { on } from '@ember/modifier';
import { fn, hash, array } from '@ember/helper';
import { t } from 'ember-intl';
import { gt, or } from 'ember-truth-helpers';
import set from 'ember-set-helper/helpers/set';
import {
  HdsFormLegend,
  HdsIcon,
  HdsTextBody,
  HdsButton,
  HdsButtonSet,
  HdsSeparator,
  HdsTextDisplay,
  HdsApplicationState,
} from '@hashicorp/design-system-components/components';
import { Flex, FlexGrid, WithErrors } from 'core/components';
import { boxPadding, boxMargin } from 'core/modifiers';
import EditPermissions from 'manage-access/components/manage-access/form/edit-permissions';
import ChangeSummary from 'manage-access/components/manage-access/table/change-summary';
import iamPolicySummaryChanges from 'manage-access/helpers/iam-policy-summary-changes';
import RoleDefinitions from 'manage-access/components/manage-access/flyout/role-definitions';

/**
 *
 * `IamGroupsProjectFormEditRoleV2` encapsulates the form used to edit a groups's role at the project level.
 *
 * ```
 *    <Iam::Groups::Project::Form::EditRole
 *        @currentRolesLocation={{@currentRolesLocation}}
 *        @currentScopeLink={{@currentScopeLink}}
 *        @role={{@role}}
 *        @project={{@project}}
 *        @policy={{@rawProjectPolicy}}
 *        @group={{@group}}
 *        @projectRoles={{@projectRoles}}
 *        @handleSubmit={{this.handleSubmitV2}}
 *        as |F|
 *      >
 *        <StickyFooter>
 *          <Hds::ButtonSet>
 *            <F.SubmitButton form={{formId}} type="submit" />
 *            <F.CancelButton />
 *          </Hds::ButtonSet>
 *        </StickyFooter>
 *      </Iam::Groups::Project::Form::EditRole>
 * ```
 *
 * @class IamGroupsProjectFormEditRoleV2
 *
 */
export default class IamGroupsProjectFormEditRoleV2 extends Component {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked editedPolicy = this.args.policy;
  @tracked showRoleDefinitionsFlyout = false;

  onPolicyChange = ({ policy }) => {
    this.editedPolicy = policy;
  };

  submit = task(async (event) => {
    event.preventDefault();

    await this.args.handleSubmit.perform(this.editedPolicy);
  });

  <template>
    <form
      data-test-iam-groups-project-form-edit-role
      novalidate
      {{on "submit" this.submit.perform}}
      id="edit-group-role-form-v2"
    >
      <WithErrors @error={{this.submit.last.error}} as |FormError|>
        <HdsFormLegend {{boxMargin "xl 0"}}>
          {{t "components.iam.groups.project.form.edit-role.legend"}}
          <Flex @direction="row" @grow={{1}} @gap="sm" {{boxMargin "sm 0"}}>
            <HdsIcon @name="file-text" @isInline={{true}} />
            <HdsTextBody @tag="span">{{@project.name}}</HdsTextBody>
          </Flex>
        </HdsFormLegend>
        {{#if this.submit.last.error}}
          <FormError />
        {{/if}}
        <FlexGrid as |FG|>
          <FG.Item @sm="12" @md="12" @lg="10" @xl="8">
            <EditPermissions
              @memberId={{@group.resourceId}}
              @memberType="GROUP"
              @policy={{@policy}}
              @roles={{@projectRoles}}
              @onChange={{this.onPolicyChange}}
              {{boxPadding "0 0 md 0"}}
            >
              <:body as |B|>
                <Flex @alignItems="end" @direction="row" @gap="sm">
                  <Flex
                    @alignItems="start"
                    @direction="row"
                    @gap="sm"
                    @basis="zero-percent"
                    @grow={{1}}
                    @shrink={{1}}
                  >
                    <B.PermissionSelect
                      @basicServiceLabel={{t
                        "components.iam.groups.project.form.edit-role.edit-assignment.project-label"
                        name=@project.name
                      }}
                    />
                  </Flex>
                  <Flex
                    @alignItems="end"
                    @direction="row"
                    @gap="sm"
                    @basis="zero-percent"
                    @grow={{1}}
                    @shrink={{1}}
                  >
                    <HdsButton
                      @color="tertiary"
                      @icon="help"
                      @iconPosition="leading"
                      @isInline={{true}}
                      @text={{t
                        "components.iam.groups.project.form.edit-role.edit-assignment.view-role-definitions"
                      }}
                      {{on "click" (set this "showRoleDefinitionsFlyout" true)}}
                      data-test-roles-chart-toggle-button
                    />
                    {{#if (gt B.data.permissions.size 1)}}
                      <HdsButton
                        @color="tertiary"
                        @icon="trash"
                        @text={{B.removePermissionText}}
                        {{on
                          "click"
                          (fn B.removePermission B.data.id B.data.permission)
                        }}
                      />
                    {{/if}}
                  </Flex>
                </Flex>
              </:body>
              <:footer as |F|>
                {{#if F.nextAvailableRole.service}}
                  <HdsButtonSet>
                    <HdsButton
                      @color="tertiary"
                      @icon="plus"
                      @text={{F.addAssignmentText}}
                      {{on
                        "click"
                        (fn
                          F.addPermission
                          (hash service=F.nextAvailableRole.service)
                        )
                      }}
                      data-test-edit-permissions-add-assignment-button
                    />
                  </HdsButtonSet>
                {{/if}}
              </:footer>
            </EditPermissions>
            <HdsSeparator />
            <Flex @direction="column" @gap="lg" @justifyContent="center">
              <HdsTextDisplay @tag="h4" @size="300" @weight="bold">
                {{t
                  "components.iam.groups.project.form.edit-role.edit-assignment.review-changes-for-group-name"
                  groupName=(or @group.displayName @group.resourceId)
                }}
              </HdsTextDisplay>
              {{#let
                (iamPolicySummaryChanges
                  @policy
                  this.editedPolicy
                  (hash memberIds=(array @group.resourceId))
                )
                as |summary|
              }}
                {{#if summary.changes}}
                  <ChangeSummary
                    @changes={{summary.changes}}
                    @roles={{@projectRoles}}
                  />
                {{else}}
                  <div
                    {{boxPadding "2xl 0"}}
                    data-test-manage-access-change-summary-application-state
                  >
                    <HdsApplicationState as |A|>
                      <A.Header
                        @title={{t
                          "components.iam.groups.project.form.edit-role.edit-assignment.no-changes-applied"
                        }}
                      />
                      <A.Body
                        @text={{t
                          "components.iam.groups.project.form.edit-role.edit-assignment.select-a-role-to-see-changes"
                        }}
                      />
                    </HdsApplicationState>
                  </div>
                {{/if}}
              {{/let}}
            </Flex>
          </FG.Item>
        </FlexGrid>
        {{#if this.showRoleDefinitionsFlyout}}
          <RoleDefinitions
            @rolesLocation={{@currentRolesLocation}}
            @currentScopeLink={{@currentScopeLink}}
            @onClose={{set this "showRoleDefinitionsFlyout" false}}
          />
        {{/if}}
      </WithErrors>

      {{yield}}

    </form>

    {{yield
      (hash
        SubmitButton=(component
          HdsButton
          color="primary"
          type="submit"
          text=(t "components.iam.groups.project.form.edit-role.buttons.submit")
        )
        CancelButton=(component
          HdsButton
          color="secondary"
          text=(t "components.iam.groups.project.form.edit-role.buttons.cancel")
          route="cloud.access-control.groups.list"
        )
      )
    }}
  </template>
}
